import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  CircularProgress,
  IconButton,
  Typography,
  Box,
  Button,
  TableSortLabel,
  TablePagination,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import debounce from "lodash.debounce";
import styled from "styled-components";
import StyledTextField from "./StyledTextField";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

const StyledPaper = styled(Paper)`
  background-color: #2b3342 !important;
  color: #fbfeff !important;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const StyledTableContainer = styled(TableContainer)`
  background-color: #2b3342 !important;
  box-shadow: none !important;
  height: calc(100vh - 360px);
  ${({ width }) => width && `width: ${width} !important;`}
  overflow-x: auto;
  table-layout: fixed;
`;

const BaseTable = ({
  title,
  columns,
  data,
  isLoading,
  onSearch = () => {},
  onSort = () => {},
  selectableRows = false,
  multiSelect = false,
  forcedSearchQuery = "",
  onRowSelect = () => {},
  onRefresh,
  onPageChange,
  totalRecords = 0,
  page = 0,
  limit = 10,
  onClearFilters = () => {},
  noSearch = false,
  width,
  expandableRows = [],
  getExpandableRowContent,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [expandedRows, setExpandedRows] = useState([]);

  const debouncedSearch = useCallback(
    debounce((query) => {
      onSearch(query);
    }, 500),
    [onSearch]
  );

  useEffect(() => {
    if (forcedSearchQuery && forcedSearchQuery !== searchTerm) {
      setSearchTerm(forcedSearchQuery);
    }
  }, [forcedSearchQuery]);

  const handleSearchChange = (e) => {
    const newQuery = e.target.value;
    setSearchTerm(newQuery);
    debouncedSearch(newQuery);

    if (newQuery === "") {
      handleClearFilters();
    }
  };

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm]);

  const handleRowSelect = (row) => {
    let updatedSelection;
    if (multiSelect) {
      if (selectedRows.some((selected) => selected.id === row.id)) {
        updatedSelection = selectedRows.filter(
          (selected) => selected.id !== row.id
        );
      } else {
        updatedSelection = [...selectedRows, row];
      }
    } else {
      updatedSelection = selectedRows.some((selected) => selected.id === row.id)
        ? []
        : [row];
    }
    setSelectedRows(updatedSelection);
    if (onRowSelect) {
      onRowSelect(updatedSelection);
    }
  };

  const handleAllRowsSelect = () => {
    if (selectedRows.length === data.length) {
      setSelectedRows([]);
      if (onRowSelect) {
        onRowSelect([]);
      }
    } else {
      setSelectedRows(data);
      if (onRowSelect) {
        onRowSelect(data);
      }
    }
  };

  const handleSort = (columnId) => {
    let newDirection = "asc";
    if (sortConfig.key === columnId && sortConfig.direction === "asc") {
      newDirection = "desc";
    }
    setSortConfig({ key: columnId, direction: newDirection });
    onSort(columnId, newDirection);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSortConfig({ key: null, direction: "asc" });
    setSelectedRows([]);
    onClearFilters();
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prevExpandedRows) => {
      if (prevExpandedRows.includes(rowId)) {
        return prevExpandedRows.filter((id) => id !== rowId); // Collapse row
      } else {
        return [...prevExpandedRows, rowId]; // Expand row
      }
    });
  };

  return (
    <StyledPaper elevation={3} sx={{ padding: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">{title}</Typography>
        <Box display="flex" alignItems="center">
          <IconButton onClick={onRefresh} sx={{ marginLeft: 1 }}>
            <RefreshIcon sx={{ color: "white" }} />
          </IconButton>
          {(searchTerm || sortConfig.key || selectedRows.length > 0) && (
            <Box
              display="flex"
              justifyContent="flex-end"
              sx={{ marginLeft: "10px" }}
            >
              <Button
                variant="contained"
                color="error"
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      {noSearch ? null : (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <StyledTextField
            size="small"
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: <SearchIcon />,
            }}
            sx={{ width: "200px" }}
          />
        </Box>
      )}
      <StyledTableContainer width={width ? "calc(100vw - 595px)" : undefined}>
        <Table>
          <TableHead>
            <TableRow>
              {selectableRows && (
                <TableCell>
                  <Checkbox
                    checked={
                      selectedRows.length === data.length && data.length > 0
                    }
                    onChange={() => handleAllRowsSelect()}
                    sx={{
                      color: "white",
                      "&.Mui-checked": {
                        color: "white",
                      },
                      "&.MuiCheckbox-root": {
                        visibility: "visible !important",
                      },
                    }}
                  />
                </TableCell>
              )}
              {expandableRows.length > 0 && (
                <TableCell>
                  <TableSortLabel
                    sx={{
                      color: "white !important",
                      "& .MuiTableSortLabel-icon": {
                        color: "white !important",
                      },
                    }}
                  >
                    #
                  </TableSortLabel>
                </TableCell>
              )}
              {columns
                .filter((col) => col.display !== "hidden")
                .map((col) => (
                  <TableCell key={col.id}>
                    <TableSortLabel
                      active={sortConfig.key === col.id}
                      direction={
                        sortConfig.key === col.id ? sortConfig.direction : "asc"
                      }
                      onClick={() => handleSort(col.id)}
                      sx={{
                        color: "white !important",
                        "& .MuiTableSortLabel-icon": {
                          color: "white !important",
                        },
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectableRows ? 1 : 0)}
                  align="center"
                >
                  <CircularProgress sx={{ color: "white" }} />
                </TableCell>
              </TableRow>
            ) : data.length > 0 ? (
              data.map((row) => (
                <>
                  <TableRow
                    key={row.id}
                    hover
                    selected={selectedRows.some(
                      (selected) => selected.id === row.id
                    )}
                    onClick={() => selectableRows && handleRowSelect(row)}
                  >
                    {selectableRows && (
                      <TableCell>
                        <Checkbox
                          checked={selectedRows.some(
                            (selected) => selected.id === row.id
                          )}
                          sx={{
                            color: "white",
                            "&.Mui-checked": {
                              color: "white",
                            },
                            "&.MuiCheckbox-root": {
                              visibility: "visible !important",
                            },
                          }}
                        />
                      </TableCell>
                    )}
                    {expandableRows.length > 0 && (
                      <TableCell>
                        {expandableRows.includes(row.id) && (
                          <IconButton
                            onClick={() => toggleRowExpansion(row.id)}
                            sx={{ color: "white", padding: "0px" }}
                          >
                            {expandedRows.includes(row.id) ? (
                              <ExpandLess />
                            ) : (
                              <ExpandMore />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                    {columns
                      .filter((col) => col.display !== "hidden")
                      .map((col) => {
                        if (col.id === "patientName") {
                          return (
                            <TableCell
                              key={col.id}
                              sx={{
                                maxWidth: "200px",
                                wordBreak: "break-word",
                                whiteSpace: "normal",
                              }}
                            >
                              {row[col.id] || "-"}
                            </TableCell>
                          );
                        }
                        if (col.id === "status") {
                          const transferStatus = row.transfer_status || "";
                          const transferProgress = row.transfer_progress || 0;
                          return (
                            <TableCell key={col.id}>
                              {transferStatus} ({transferProgress}%)
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={col.id}>
                            {typeof row[col.id] === "boolean"
                              ? row[col.id].toString()
                              : row[col.id] || "-"}
                          </TableCell>
                        );
                      })}
                  </TableRow>
                  {expandedRows.includes(row.id) && (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length + (selectableRows ? 1 : 0)}
                      >
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Key</TableCell>
                              <TableCell>Value</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getExpandableRowContent(row.id)}{" "}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    (selectableRows ? 1 : 0) +
                    (expandableRows.length > 0 ? 1 : 0)
                  }
                  align="center"
                >
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
      <TableRow>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalRecords}
          rowsPerPage={limit}
          page={page}
          onPageChange={(event, newPage) => onPageChange(newPage, limit)}
          onRowsPerPageChange={(event) =>
            onPageChange(0, parseInt(event.target.value, 10))
          }
          slotProps={{
            select: {
              MenuProps: {
                PaperProps: {
                  sx: {
                    backgroundColor: "#141b25",
                    color: "#FBFEFF",
                  },
                },
              },
            },
          }}
        />
      </TableRow>
    </StyledPaper>
  );
};

export default BaseTable;
